import { useState, useRef, useEffect, useCallback } from 'react'
import { useVideoRecorder } from '../../hooks/useVideoRecorder'
import { videoService } from '../../services'
import { useAuthStore } from '../../stores'
import { Button, Spinner } from '../common'
import { useToast } from '../common/Toast'

interface VideoRecorderProps {
  isOpen: boolean
  onClose: () => void
  onUploadComplete?: (videoId: string) => void
  maxDurationSeconds?: number
}

type UploadStatus = 'idle' | 'uploading' | 'processing' | 'complete' | 'error'

export function VideoRecorder({
  isOpen,
  onClose,
  onUploadComplete,
  maxDurationSeconds = 180,
}: VideoRecorderProps) {
  const { profile } = useAuthStore()
  const toast = useToast()

  const videoPreviewRef = useRef<HTMLVideoElement>(null)
  const playbackRef = useRef<HTMLVideoElement>(null)

  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [videoTitle, setVideoTitle] = useState('')
  const [showTitleInput, setShowTitleInput] = useState(false)

  const {
    status,
    error,
    previewStream,
    recordedBlob,
    recordedUrl,
    duration,
    startCamera,
    stopCamera,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    hasPermission,
  } = useVideoRecorder({
    maxDurationSeconds,
    onMaxDurationReached: () => {
      toast.info('Maximum recording duration reached')
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })

  // Attach preview stream to video element
  useEffect(() => {
    if (videoPreviewRef.current && previewStream) {
      videoPreviewRef.current.srcObject = previewStream
    }
  }, [previewStream])

  // Start camera when modal opens
  useEffect(() => {
    if (isOpen && status === 'idle') {
      startCamera()
    }
  }, [isOpen])

  // Cleanup when modal closes
  useEffect(() => {
    if (!isOpen) {
      stopCamera()
      resetRecording()
      setUploadStatus('idle')
      setUploadProgress(0)
      setVideoTitle('')
      setShowTitleInput(false)
    }
  }, [isOpen])

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const remainingTime = maxDurationSeconds - duration

  const handleUpload = useCallback(async () => {
    if (!recordedBlob || !profile?.id) return

    const title = videoTitle.trim() || `Recording ${new Date().toLocaleString()}`

    setUploadStatus('uploading')
    setUploadProgress(0)

    try {
      // Step 1: Get upload URL from our edge function
      const { uploadUrl, videoId } = await videoService.getUploadUrl()

      // Step 2: Upload the blob to Mux
      const xhr = new XMLHttpRequest()
      xhr.open('PUT', uploadUrl)

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100)
          setUploadProgress(progress)
        }
      }

      await new Promise<void>((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`))
          }
        }
        xhr.onerror = () => reject(new Error('Upload failed'))
        xhr.send(recordedBlob)
      })

      setUploadStatus('processing')

      // Step 3: Create video record in database
      await videoService.createVideoFromUpload({
        mux_upload_id: videoId,
        title,
        user_id: profile.id,
      })

      setUploadStatus('complete')
      toast.success('Video uploaded successfully!')

      // Wait a moment then call completion callback
      setTimeout(() => {
        onUploadComplete?.(videoId)
        onClose()
      }, 1500)
    } catch (err) {
      console.error('Upload error:', err)
      setUploadStatus('error')
      toast.error('Failed to upload video')
    }
  }, [recordedBlob, profile, videoTitle, onUploadComplete, onClose, toast])

  if (!isOpen) return null

  const renderCameraPermissionRequest = () => (
    <div className="flex flex-col items-center justify-center h-full py-12">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Camera Access Required</h3>
      <p className="text-gray-500 text-center mb-6 max-w-sm">
        We need access to your camera and microphone to record exercise videos.
      </p>
      <Button onClick={startCamera}>
        Allow Camera Access
      </Button>
    </div>
  )

  const renderError = () => (
    <div className="flex flex-col items-center justify-center h-full py-12">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Camera Error</h3>
      <p className="text-gray-500 text-center mb-6 max-w-sm">
        {error?.message || 'Unable to access camera. Please check your permissions and try again.'}
      </p>
      <Button onClick={startCamera}>
        Try Again
      </Button>
    </div>
  )

  const renderAcquiring = () => (
    <div className="flex flex-col items-center justify-center h-full py-12">
      <Spinner size="lg" />
      <p className="mt-4 text-gray-500">Connecting to camera...</p>
    </div>
  )

  const renderRecordingUI = () => (
    <div className="relative">
      {/* Live preview */}
      <video
        ref={videoPreviewRef}
        autoPlay
        playsInline
        muted
        className="w-full aspect-video bg-black rounded-lg object-cover transform -scale-x-100"
      />

      {/* Recording indicator */}
      {status === 'recording' && (
        <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
          <span className="text-sm font-medium">REC</span>
        </div>
      )}

      {/* Duration / remaining time */}
      <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-mono">
        {status === 'recording' || status === 'paused' ? (
          <span className={remainingTime <= 30 ? 'text-red-400' : ''}>
            {formatDuration(duration)} / {formatDuration(maxDurationSeconds)}
          </span>
        ) : (
          <span>Max {formatDuration(maxDurationSeconds)}</span>
        )}
      </div>

      {/* Paused overlay */}
      {status === 'paused' && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
          <span className="text-white text-xl font-medium">Paused</span>
        </div>
      )}
    </div>
  )

  const renderPlayback = () => (
    <div className="relative">
      <video
        ref={playbackRef}
        src={recordedUrl || undefined}
        controls
        className="w-full aspect-video bg-black rounded-lg"
      />

      {/* Title input */}
      {showTitleInput && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Video Title
          </label>
          <input
            type="text"
            value={videoTitle}
            onChange={(e) => setVideoTitle(e.target.value)}
            placeholder="Enter a title for your video..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={100}
          />
        </div>
      )}
    </div>
  )

  const renderUploadProgress = () => (
    <div className="flex flex-col items-center justify-center py-12">
      {uploadStatus === 'uploading' && (
        <>
          <div className="w-full max-w-xs bg-gray-200 rounded-full h-2 mb-4">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-gray-600">Uploading... {uploadProgress}%</p>
        </>
      )}
      {uploadStatus === 'processing' && (
        <>
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Processing video...</p>
        </>
      )}
      {uploadStatus === 'complete' && (
        <>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-gray-900 font-medium">Upload Complete!</p>
        </>
      )}
      {uploadStatus === 'error' && (
        <>
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <p className="text-gray-900 font-medium mb-2">Upload Failed</p>
          <Button variant="outline" onClick={() => setUploadStatus('idle')}>
            Try Again
          </Button>
        </>
      )}
    </div>
  )

  const renderControls = () => {
    // Don't show controls during upload
    if (uploadStatus !== 'idle') return null

    // Playback controls (after recording)
    if (status === 'stopped' && recordedUrl) {
      return (
        <div className="flex items-center justify-center gap-3 pt-4">
          <Button variant="outline" onClick={resetRecording}>
            Record Again
          </Button>
          {!showTitleInput ? (
            <Button onClick={() => setShowTitleInput(true)}>
              Continue to Upload
            </Button>
          ) : (
            <Button onClick={handleUpload}>
              Upload Video
            </Button>
          )}
        </div>
      )
    }

    // Recording controls
    if (status === 'ready' || status === 'recording' || status === 'paused') {
      return (
        <div className="flex items-center justify-center gap-4 pt-4">
          {status === 'ready' && (
            <button
              onClick={startRecording}
              className="w-16 h-16 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-colors"
              title="Start Recording"
            >
              <div className="w-6 h-6 bg-white rounded-sm" />
            </button>
          )}

          {status === 'recording' && (
            <>
              <button
                onClick={pauseRecording}
                className="w-12 h-12 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center transition-colors"
                title="Pause"
              >
                <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              </button>
              <button
                onClick={stopRecording}
                className="w-16 h-16 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-colors"
                title="Stop Recording"
              >
                <div className="w-6 h-6 bg-white rounded-sm" />
              </button>
            </>
          )}

          {status === 'paused' && (
            <>
              <button
                onClick={resumeRecording}
                className="w-12 h-12 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center transition-colors"
                title="Resume"
              >
                <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
              <button
                onClick={stopRecording}
                className="w-16 h-16 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-colors"
                title="Stop Recording"
              >
                <div className="w-6 h-6 bg-white rounded-sm" />
              </button>
            </>
          )}
        </div>
      )
    }

    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={uploadStatus === 'idle' ? onClose : undefined} />

      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Record Video</h2>
          {uploadStatus === 'idle' && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {/* Permission denied or not yet requested */}
          {hasPermission === false && status !== 'acquiring' && renderCameraPermissionRequest()}

          {/* Acquiring camera */}
          {status === 'acquiring' && renderAcquiring()}

          {/* Error state */}
          {status === 'error' && renderError()}

          {/* Ready/recording states */}
          {(status === 'ready' || status === 'recording' || status === 'paused') && renderRecordingUI()}

          {/* Playback state */}
          {status === 'stopped' && recordedUrl && uploadStatus === 'idle' && renderPlayback()}

          {/* Upload progress */}
          {uploadStatus !== 'idle' && renderUploadProgress()}
        </div>

        {/* Controls */}
        <div className="p-4 border-t border-gray-200">
          {renderControls()}

          {/* Tips */}
          {status === 'ready' && uploadStatus === 'idle' && (
            <p className="text-center text-sm text-gray-500 mt-3">
              Click the record button to start. Max {maxDurationSeconds / 60} minutes.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
